/**
 * Rewrites a Button `styleOverride` so it applies to one button instance only. The override is
 * written as if it lived in the button's own shadow root, so `:host` stands for the button root:
 *
 *   `a.mp-button-container {…}`            -> `[instance] a.mp-button-container {…}`
 *   `:host(:focus) .mp-button-container {…}` -> `[instance]:focus .mp-button-container {…}`
 *   `:host-context(html.dark-mode) a {…}`  -> `html.dark-mode [instance] a {…}`, or
 *                                             `:host-context(html.dark-mode) [instance] a {…}` when
 *                                             the button renders inside a shadow root, whose host
 *                                             is the only element `:host-context` can test.
 *
 * At-rule preludes are left alone; the style rules nested in them are scoped.
 */
export function scopeStyleOverride(styleOverride: string, instanceSelector: string, inShadowRoot: boolean): string {
  return styleOverride.replace(/([^{}]+)\{/g, (rule, selectorList: string) => {
    if (selectorList.trim().startsWith(`@`)) {
      return rule;
    }
    const leadingWhitespace = /^\s*/.exec(selectorList)?.[0] ?? ``;
    const scopedSelectors = splitSelectorList(selectorList).map((selector) =>
      scopeSelector(selector.trim(), instanceSelector, inShadowRoot),
    );
    return `${leadingWhitespace}${scopedSelectors.join(`, `)} {`;
  });
}

function scopeSelector(selector: string, instanceSelector: string, inShadowRoot: boolean): string {
  if (selector.includes(`:host-context(`)) {
    return selector.replace(/:host-context\(([^)]+)\)/g, (_, context: string) =>
      inShadowRoot ? `:host-context(${context}) ${instanceSelector}` : `${context} ${instanceSelector}`,
    );
  }
  if (selector.includes(`:host`)) {
    return selector.replace(
      /:host(?:\(([^)]+)\))?/g,
      (_, hostCondition: string | undefined) => `${instanceSelector}${hostCondition ?? ``}`,
    );
  }
  return `${instanceSelector} ${selector}`;
}

/** Splits on the commas that separate selectors, not the ones inside `:is(a, b)`-style arguments. */
function splitSelectorList(selectorList: string): string[] {
  const selectors: string[] = [];
  let depth = 0;
  let start = 0;
  for (let index = 0; index < selectorList.length; index++) {
    const character = selectorList[index];
    if (character === `(`) {
      depth++;
    } else if (character === `)`) {
      depth--;
    } else if (character === `,` && depth === 0) {
      selectors.push(selectorList.slice(start, index));
      start = index + 1;
    }
  }
  selectors.push(selectorList.slice(start));
  return selectors;
}
